import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { DriverTaskKpiRow } from '@klareospace/pharos-lib-types';
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  LoadingState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@klareospace/pharos-lib-web-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const INITIAL_VISIBLE_ROWS = 5;
const SHOW_MORE_STEP = 5;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DriverKpisTodayProps {
  isError: boolean;
  isLoading: boolean;
  rows: DriverTaskKpiRow[] | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Presentational display of today's `driver-task-kpis` KPI — card-wrapped table of drivers with a
 * task today. Data fetching, feature-flag/permission gating, and realtime sync live in
 * `useDriverKpisToday`.
 */
export function DriverKpisToday({ isError, isLoading, rows }: DriverKpisTodayProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('kpis');
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_ROWS);

  function handleRowClick(row: DriverTaskKpiRow): void {
    void navigate({ search: { driverId: row.driverId, driverName: row.driverName }, to: '/orders' });
  }

  function renderBody(): React.ReactNode {
    if (isError) return <ErrorBanner message={t('driverTable.loadError')} />;
    if (isLoading || !rows) return <LoadingState message={t('driverTable.waiting')} />;
    if (rows.length === 0) {
      return <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('driverTable.empty')}</p>;
    }

    const visibleRows = rows.slice(0, visibleCount);
    const hasMore = visibleCount < rows.length;

    return (
      <>
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="bg-muted/30 text-muted-foreground">
              <TableHead className="w-[16%] py-2.5">{tCommon('fields.driverColumnLabel')}</TableHead>
              <TableHead className="w-[20%] px-3 py-2.5">{t('driverTable.columns.pickupSites')}</TableHead>
              <TableHead className="px-3 py-2.5 text-center">{t('driverTable.columns.assigned')}</TableHead>
              <TableHead className="px-3 py-2.5 text-center">{tCommon('statuses.inProgress')}</TableHead>
              <TableHead className="px-3 py-2.5 text-center">{t('driverTable.columns.ontime')}</TableHead>
              <TableHead className="px-3 py-2.5 text-center">{t('driverTable.columns.late')}</TableHead>
              <TableHead className="px-3 py-2.5 text-center">{t('common.failed')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow className="cursor-pointer" key={row.driverId} onClick={(): void => handleRowClick(row)}>
                <TableCell className="truncate px-4 py-2.5 font-medium">
                  <Link
                    onClick={(event): void => event.stopPropagation()}
                    search={{ driverId: row.driverId, driverName: row.driverName }}
                    to="/orders"
                  >
                    {row.driverName}
                  </Link>
                </TableCell>
                <TableCell className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {row.pickupSiteNames.map((siteName) => (
                      <Badge className="font-normal" key={siteName} variant="secondary">
                        {siteName}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="px-3 py-2.5 text-center">{row.assigned}</TableCell>
                <TableCell className="px-3 py-2.5 text-center">{row.inprogress}</TableCell>
                <TableCell className="px-3 py-2.5 text-center">{row.ontime}</TableCell>
                <TableCell className="px-3 py-2.5 text-center">{row.late}</TableCell>
                <TableCell className="px-3 py-2.5 text-center">{row.failed}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {hasMore && (
          <div className="border-t border-border px-3 py-2">
            <Button
              className="text-primary"
              onClick={(): void => setVisibleCount((count) => count + SHOW_MORE_STEP)}
              size="sm"
              type="button"
              variant="ghost"
            >
              {t('driverTable.showMore')}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <Card className="overflow-hidden rounded-lg p-0">
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t('driverTable.title')}</h2>
      </div>
      {renderBody()}
    </Card>
  );
}
